import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  doc,
  setDoc,
  collection,
  addDoc,
  deleteDoc,
  updateDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useUserStore } from "./userStore";

export interface TradeAccount {
  id: string;
  exchange: string;
  exchangeType: "SPOT" | "PERPETUAL";
  apiKey: string;
  apiSecret: string;
  trading: boolean;
  journal: boolean;
  testnet: boolean;
  label?: string;
  ifNewUser: boolean;
  rebateWorking: boolean;
  createdAt: number;
  updatedAt: number;
}

interface TradeAccountsState {
  accounts: TradeAccount[];
  selectedAccountId: string;
  setAccounts: (accounts: TradeAccount[]) => void;
  setSelectedAccountId: (id: string) => void;
  addAccount: (
    account: Omit<TradeAccount, "id" | "createdAt" | "updatedAt">,
  ) => Promise<void>;
  updateAccount: (id: string, updates: Partial<TradeAccount>) => Promise<void>;
  removeAccount: (id: string) => Promise<void>;
  syncWithFirebase: (uid: string) => Promise<void>;
}

export const useTradeAccountsStore = create<TradeAccountsState>()(
  persist(
    (set, get) => ({
      accounts: [],
      selectedAccountId: "",
      setSelectedAccountId: (id) => set({ selectedAccountId: id }),
      setAccounts: (accounts) => set({ accounts }),
      addAccount: async (accountData) => {
        const accountWithoutId = {
          ...accountData,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };

        try {
          const user = useUserStore.getState().user;
          if (!user?.uid) {
            throw new Error("User not authenticated");
          }

          // Add to Firebase
          const accountsCollection = collection(
            db,
            `users/${user.uid}/tradeAccounts`,
          );
          const docRef = await addDoc(accountsCollection, accountWithoutId);

          // Create complete account with Firestore ID
          const newAccount: TradeAccount = {
            ...accountWithoutId,
            id: docRef.id,
          };

          // Update local state
          set((state) => ({
            accounts: [...state.accounts, newAccount],
          }));
        } catch (error) {
          console.error("Error adding trade account:", error);
          throw error;
        }
      },
      updateAccount: async (id, updates) => {
        try {
          const user = useUserStore.getState().user;
          if (!user?.uid) {
            throw new Error("User not authenticated");
          }

          // Ensure new fields have default values
          const updatesWithDefaults = {
            ...updates,
            trading: updates.trading ?? false,
            journal: updates.journal ?? false,
            testnet: updates.testnet ?? false,
            ifNewUser: updates.ifNewUser ?? false,
            rebateWorking: updates.rebateWorking ?? false,
            updatedAt: Date.now(),
          };

          // Update in Firebase
          const accountRef = doc(db, `users/${user.uid}/tradeAccounts/${id}`);
          await updateDoc(accountRef, updatesWithDefaults);

          // Update local state
          set((state) => ({
            accounts: state.accounts.map((account) =>
              account.id === id
                ? { ...account, ...updatesWithDefaults }
                : account,
            ),
          }));
        } catch (error) {
          console.error("Error updating trade account:", error);
          throw error;
        }
      },
      removeAccount: async (id) => {
        try {
          const user = useUserStore.getState().user;
          if (!user?.uid) {
            throw new Error("User not authenticated");
          }

          // Remove from Firebase
          const accountRef = doc(db, `users/${user.uid}/tradeAccounts/${id}`);
          await deleteDoc(accountRef);

          // Update local state
          set((state) => ({
            accounts: state.accounts.filter((account) => account.id !== id),
          }));
        } catch (error) {
          console.error("Error removing trade account:", error);
          throw error;
        }
      },
      syncWithFirebase: async (uid: string) => {
        try {
          // Get accounts from Firebase
          const accountsCollection = collection(
            db,
            `users/${uid}/tradeAccounts`,
          );
          const snapshot = await getDocs(accountsCollection);

          const firestoreAccounts = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          })) as TradeAccount[];

          // Update local state with Firebase data
          set({ accounts: firestoreAccounts });
        } catch (error) {
          console.error("Error syncing trade accounts with Firebase:", error);
          throw error;
        }
      },
    }),
    {
      name: "sf-trade-accounts-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
